// solarPhase.js — shared engine for "theme follows the sun".
//
// Given the user's lat/lng (from geolocation) and the current Date, returns
// one of 8 phases:
//   pre-dawn  — deep night, before astronomical dawn
//   dawn      — horizon glowing, sun below horizon
//   morning   — after sunrise, before midday
//   noon      — solar noon ± window
//   afternoon — after noon, before golden hour
//   golden    — golden hour before sunset
//   dusk      — after sunset, still light
//   night     — full dark, after civil dusk
//
// Works everywhere suncalc runs — pure JS, offline. Season handling is
// automatic because sunrise/sunset naturally shift with date + latitude.
//
// Fallback: if no location provided, uses a reasonable clock-only schedule
// anchored to Indian Standard Time (IST) — roughly matching Delhi average.

import SunCalc from 'suncalc';

// Clock-only fallback (IST). Used when geolocation is denied or unavailable.
// Windows mirror typical Indian sunrise/sunset so the fallback looks natural.
function clockPhase(date) {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h < 4.5)           return 'pre-dawn';
  if (h < 6.0)           return 'dawn';
  if (h < 11.0)          return 'morning';
  if (h < 13.5)          return 'noon';
  if (h < 16.5)          return 'afternoon';
  if (h < 18.0)          return 'golden';
  if (h < 19.5)          return 'dusk';
  return 'night';
}

export function detectPhase(date, coords) {
  if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
    return clockPhase(date);
  }
  const times = SunCalc.getTimes(date, coords.lat, coords.lng);
  const t = date.getTime();
  // Fallback to clock if suncalc returned Invalid Date (e.g. polar regions).
  const ok = (x) => x instanceof Date && !isNaN(x.getTime());
  if (!ok(times.sunrise) || !ok(times.sunset) || !ok(times.solarNoon)) {
    return clockPhase(date);
  }
  // Assemble boundaries, handling the tricky timezone wrap at local midnight.
  const bounds = {
    nightEnd:     times.nightEnd?.getTime() || times.nauticalDawn?.getTime() || (times.dawn?.getTime() - 30 * 60000),
    dawn:         times.dawn?.getTime()     || times.sunriseEnd?.getTime() - 45 * 60000,
    sunrise:      times.sunrise.getTime(),
    sunriseEnd:   times.sunriseEnd?.getTime() || times.sunrise.getTime() + 30 * 60000,
    solarNoon:    times.solarNoon.getTime(),
    goldenHour:   times.goldenHour?.getTime() || times.sunset.getTime() - 60 * 60000,
    sunsetStart:  times.sunsetStart?.getTime() || times.sunset.getTime() - 15 * 60000,
    sunset:       times.sunset.getTime(),
    dusk:         times.dusk?.getTime() || times.sunset.getTime() + 30 * 60000,
    night:        times.night?.getTime() || times.nauticalDusk?.getTime() || (times.sunset.getTime() + 60 * 60000),
  };

  if (t < bounds.nightEnd)                                return 'pre-dawn';
  if (t < bounds.sunrise)                                 return 'dawn';
  if (t < bounds.solarNoon - 45 * 60000)                  return 'morning';
  if (t < bounds.solarNoon + 45 * 60000)                  return 'noon';
  if (t < bounds.goldenHour)                              return 'afternoon';
  if (t < bounds.sunset)                                  return 'golden';
  if (t < bounds.night)                                   return 'dusk';
  return 'night';
}

// Returns the next phase boundary after `date` — lets the theme provider
// schedule a single setTimeout instead of polling every second. Boundaries
// only change a few times a day, so this is cheap.
export function nextBoundary(date, coords) {
  // We sample the rest of today in 5-min steps looking for the next phase
  // flip. Cheaper than parsing every suncalc key + handling wrap cases.
  const current = detectPhase(date, coords);
  const d = new Date(date);
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  while (d < end) {
    d.setMinutes(d.getMinutes() + 5);
    if (detectPhase(d, coords) !== current) return d;
  }
  // No change today → wake up after midnight tomorrow.
  const tomorrow = new Date(date);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 5, 0, 0);
  return tomorrow;
}

// Human-friendly label for a phase, useful in tooltips/settings.
export function phaseLabel(phase) {
  return ({
    'pre-dawn':  'Pre-dawn',
    'dawn':      'Dawn',
    'morning':   'Morning',
    'noon':      'Midday',
    'afternoon': 'Afternoon',
    'golden':    'Golden hour',
    'dusk':      'Dusk',
    'night':     'Night',
  })[phase] || phase;
}
